import { ComboboxProps, MultiSelect, SelectOption } from '@chronosphereio/chrono-ui';
import { useFeatureFlags } from '@chronosphereio/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { VariableOption } from '../../../core';
import { ensureOnlyAllValueIsSelected } from '../../context/TemplateVariableProvider/utils';
import { useExtraOption } from './useExtraSelectOption';
import { VariableCombobox } from './VariableCombobox';
import { INPUT_MAX_WIDTH, INPUT_MIN_WIDTH, useTemplateVariableOptions } from './variable-options';

export interface MultiSelectVariableProps {
  title: string;
  options?: VariableOption[];
  isLoading: boolean;
  allowAllValue: boolean;
  value: string[] | null;
  onChange: (nextValue: string[]) => void;
  disabled?: boolean;
  renderFooter?: ComboboxProps['renderFooter'];
}

/**
 * Input for selecting variable values from a list of options where multiple values are allowed to be selected from
 * the list.
 */
export function MultiSelectVariable(props: MultiSelectVariableProps) {
  const { enableNewVariableUx: enableNewTemplateVariables } = useFeatureFlags();
  const { title, options, isLoading, allowAllValue, value, onChange, disabled } = props;

  const { optionsByKey, popperMinWidth, selectOptions, hasLoadedOptions } = useTemplateVariableOptions(
    allowAllValue,
    true,
    options
  );
  const [multiSelectOpen, setMultiSelectOpen] = useState(false);
  // Use a temp value to store the selected options while the user is typing
  const [tempValues, setTempValues] = useState<string[] | null>(value);
  const [prevTempValues, setPrevTempValues] = useState(value);
  const [inputWidth, setInputWidth] = useState(0);

  // Update the temp value when the value changes
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  if (value !== prevTempValues) {
    setPrevTempValues(value);
    setTempValues(value);
  }

  // Use the map to find the SelectOption that are selected using the values as keys
  const multiSelectValues: SelectOption[] = useMemo(() => {
    const selected: SelectOption[] = [];

    // If the value is null (not sure when this could happen?), just return an empty array so we don't pass undefined
    // and switch the component between being uncontrolled and controlled
    if (tempValues === null) return selected;

    // Try to find the option in our index for each selected key
    for (const selectedKey of tempValues) {
      const found = optionsByKey.get(selectedKey);
      if (found) {
        selected.push(found);
      } else {
        // If the key isn't found, just add a dummy option (TODO: Is this correct?)
        selected.push({ key: selectedKey, label: selectedKey });
      }
    }
    return selected;
  }, [optionsByKey, tempValues]);

  useEffect(() => {
    // Don't change values while loading
    if (isLoading) return;

    // If we don't have a selection, but we have options, select the first option
    if (multiSelectValues.length === 0 && selectOptions.length > 0 && selectOptions[0] !== undefined) {
      onChange([selectOptions[0].key]);
    }
  }, [selectOptions, multiSelectValues, onChange, isLoading, hasLoadedOptions, optionsByKey]);

  // Extra option is added to be an entry for what the use has typed
  const { setExtraOption, onInputChangeHandler, optionsWithExtra, filterOptions } = useExtraOption(
    selectOptions,
    multiSelectValues
  );

  // Update the temp values and the actual values
  // If tempOnly is true, only update the temp values
  const updateValue = (values: string[] | null, tempOnly = false) => {
    setTempValues(values);
    setExtraOption(''); // Reset the extra option back to default wheneve a value is selected
    if (!tempOnly) {
      onChange(values ?? []);
    }
  };

  // Set the popper width to whichever is larger: the width of the input or the width of the largest item
  // in the dropdown (popperMinWidth). This ensures that the popper's width is at least the minimum width,
  // but can grow to match the input element's width if it is larger.
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (inputRef.current !== null) {
      setInputWidth(inputRef.current.offsetWidth);
    }
  }, [multiSelectOpen]);
  const popperWidth = Math.max(inputWidth, popperMinWidth);

  const comboboxOptions = useMemo(() => {
    return selectOptions.map((d) => ({
      label: d.label,
      value: d.key,
    }));
  }, [selectOptions]);

  return (
    <>
      {!enableNewTemplateVariables && (
        <MultiSelect
          filterOptions={filterOptions}
          open={multiSelectOpen}
          onClose={() => {
            setMultiSelectOpen(false);
            updateValue(tempValues);
          }}
          onOpen={() => setMultiSelectOpen(true)}
          label={title}
          options={optionsWithExtra}
          isLoading={isLoading}
          disabled={disabled || isLoading}
          values={multiSelectValues}
          onChange={(e, next) => {
            // Map the SelectOptions back to their key values only
            const newValues = next.map((opt) => opt.key);
            const tempValues = ensureOnlyAllValueIsSelected(newValues);

            // If the dropdown is open, only update the temp values
            if (multiSelectOpen) {
              updateValue(tempValues, true);
            } else {
              updateValue(tempValues);
            }
          }}
          sx={{
            minWidth: INPUT_MIN_WIDTH,
            maxWidth: INPUT_MAX_WIDTH,
          }}
          variant="list"
          popperWidth={`${popperWidth}px`}
          InputProps={{ ref: inputRef, onChange: onInputChangeHandler }}
        />
      )}
      {enableNewTemplateVariables && (
        <VariableCombobox
          isMulti
          label={title}
          options={comboboxOptions}
          isLoading={isLoading}
          disabled={disabled}
          value={multiSelectValues.map((d) => d.key)}
          onSelect={(e) => {
            if (typeof e === 'string') {
              updateValue([e]);
            } else if (e === null) {
              updateValue(null);
            } else {
              updateValue(e);
            }
          }}
          renderFooter={props.renderFooter}
        />
      )}
    </>
  );
}
